import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Users,
    MessageSquare,
    Clock,
    CheckCircle2,
    Search,
    Plus,
    ExternalLink,
    Shield,
    FileText,
    ArrowRight,
    Activity
} from 'lucide-react';
import { useFracas } from '../contexts/FracasContext';

const CustomerPortal: React.FC = () => {
    const navigate = useNavigate();
    const { incidents } = useFracas();
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');

    // Filter incidents (mocking customer-owned incidents for now)
    const customerIncidents = incidents.slice(0, 5); // Just a demo subset

    const stats = [
        { label: 'Active Cases', value: '12', icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50' },
        { label: 'Resolved This Month', value: '28', icon: CheckCircle2, color: 'text-emerald-600', bg: 'bg-emerald-50' },
        { label: 'Pending Feedback', value: '4', icon: MessageSquare, color: 'text-blue-600', bg: 'bg-blue-50' },
        { label: 'Avg. Resolution', value: '4.2d', icon: Activity, color: 'text-purple-600', bg: 'bg-purple-50' },
    ];

    return (
        <div className="p-8 max-w-7xl mx-auto space-y-8 animate-in fade-in duration-500">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
                        <Users className="w-8 h-8 text-blue-600" />
                        Customer Collaboration Portal
                    </h1>
                    <p className="text-gray-500 dark:text-gray-400 mt-2">
                        View status updates, respond to inquiries, and track shared quality investigations.
                    </p>
                </div>
                <button
                    onClick={() => navigate('/fracas/create')}
                    className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl shadow-lg transition-all transform hover:scale-105"
                >
                    <Plus className="w-5 h-5" />
                    Report New Incident
                </button>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {stats.map((stat, idx) => (
                    <div key={idx} className="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex items-center gap-4">
                            <div className={`p-3 rounded-xl ${stat.bg} dark:bg-opacity-10`}>
                                <stat.icon className={`w-6 h-6 ${stat.color}`} />
                            </div>
                            <div>
                                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{stat.label}</p>
                                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stat.value}</p>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Main Content Area */}
            <div className="grid lg:grid-cols-3 gap-8">
                {/* Incidents Table */}
                <div className="lg:col-span-2 bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-sm overflow-hidden text-[var(--text-primary)]">
                    <div className="p-6 border-b border-gray-100 dark:border-slate-700 flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white">Active Case Tracking</h2>
                        <div className="flex items-center gap-3">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search cases..."
                                    className="pl-10 pr-4 py-2 bg-gray-50 dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                            <select
                                className="px-3 py-2 bg-gray-50 dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg text-sm outline-none"
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                            >
                                <option>All Status</option>
                                <option>Open</option>
                                <option>In Investigation</option>
                                <option>8D Pending</option>
                                <option>Resolved</option>
                            </select>
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead className="bg-gray-50 dark:bg-slate-900/50">
                                <tr>
                                    <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Reference</th>
                                    <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Issue Title</th>
                                    <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                                    <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Last Sync</th>
                                    <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                                {customerIncidents.map((incident, idx) => (
                                    <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-slate-700/30 transition-colors">
                                        <td className="px-6 py-4">
                                            <span className="font-mono text-xs font-bold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 px-2 py-1 rounded">
                                                {incident.fracasNumber}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate max-w-[200px]">
                                                {incident.title}
                                            </p>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${incident.status === 'Open' ? 'bg-amber-100 text-amber-800' :
                                                incident.status === 'Closed' ? 'bg-emerald-100 text-emerald-800' :
                                                    'bg-blue-100 text-blue-800'
                                                }`}>
                                                {incident.status}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                                            {incident.updatedAt?.toLocaleDateString() || 'Recently'}
                                        </td>
                                        <td className="px-6 py-4">
                                            <button
                                                onClick={() => navigate(`/fracas/${incident.id}`)}
                                                className="text-blue-600 hover:text-blue-700 dark:text-blue-400 font-medium text-sm flex items-center gap-1"
                                            >
                                                Details <ExternalLink className="w-3 h-3" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="p-4 border-t border-gray-100 dark:border-slate-700 text-center">
                        <button
                            onClick={() => navigate('/fracas')}
                            className="text-sm text-gray-500 hover:text-blue-600 font-medium"
                        >
                            View All Activity History
                        </button>
                    </div>
                </div>

                {/* Sidebar Cards */}
                <div className="space-y-6">
                    {/* Compliance Trust Box */}
                    <div className="bg-gradient-to-br from-slate-800 to-slate-900 p-6 rounded-2xl text-white shadow-xl relative overflow-hidden">
                        <Shield className="absolute -right-4 -bottom-4 w-32 h-32 text-white/5" />
                        <h3 className="text-lg font-bold flex items-center gap-2 mb-2">
                            <Shield className="w-5 h-5 text-blue-400" />
                            Data Security
                        </h3>
                        <p className="text-sm text-slate-400 mb-4">
                            All data shared with the customer portal is encrypted at rest and in transit (ISO 27001).
                        </p>
                        <div className="flex gap-2">
                            <span className="text-[10px] px-2 py-1 bg-white/10 rounded-md uppercase font-bold tracking-widest">TLS 1.3</span>
                            <span className="text-[10px] px-2 py-1 bg-white/10 rounded-md uppercase font-bold tracking-widest">AES-256</span>
                        </div>
                    </div>

                    {/* Pending Documentation */}
                    <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-sm">
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                            <FileText className="w-5 h-5 text-blue-600" />
                            8D Reports Awaiting Review
                        </h3>
                        <div className="space-y-4">
                            {[1, 2].map((i) => (
                                <div key={i} className="group p-3 hover:bg-gray-50 dark:hover:bg-slate-700/50 rounded-xl transition-colors cursor-pointer border border-transparent hover:border-gray-100 dark:hover:border-slate-600">
                                    <div className="flex items-center justify-between mb-1">
                                        <span className="text-xs font-mono text-gray-500">8D-2026-00{i}</span>
                                        <span className="text-[10px] text-amber-500 font-bold uppercase">Action Required</span>
                                    </div>
                                    <p className="text-sm font-semibold text-gray-900 dark:text-white group-hover:text-blue-600">Verification of Corrective Action</p>
                                    <div className="flex items-center gap-2 mt-2">
                                        <div className="w-full bg-gray-100 dark:bg-slate-700 h-1.5 rounded-full overflow-hidden">
                                            <div className="bg-blue-600 h-full w-[75%]" />
                                        </div>
                                        <span className="text-[10px] font-bold text-gray-400 whitespace-nowrap">D5 Stage</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <button
                            onClick={() => navigate('/fracas')}
                            className="w-full mt-6 py-2 text-sm font-bold text-blue-600 border border-blue-600/20 rounded-lg hover:bg-blue-50 transition-colors"
                        >
                            Manage All Reports
                        </button>
                    </div>

                    {/* Support Contact */}
                    <div className="p-6 rounded-2xl bg-blue-50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-800/20">
                        <h4 className="font-bold text-blue-900 dark:text-blue-400 mb-2">Need dedicated help?</h4>
                        <p className="text-sm text-blue-800/70 dark:text-blue-400/60 mb-4">Your account manager is usually available Mon-Fri 9am-5pm EST.</p>
                        <button
                            onClick={() => window.location.href = 'mailto:support@livingreliability.com'}
                            className="flex items-center gap-2 text-sm font-bold text-blue-700 dark:text-blue-400 hover:gap-3 transition-all"
                        >
                            Chat with Support <ArrowRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CustomerPortal;
